import React from 'react';
import { Phone, FileText, CheckCircle, DollarSign, AlertTriangle, ClipboardCheck } from 'lucide-react';
export function CdlTypesPage() {
  return <div className="min-h-screen bg-gray-50" data-id="element-2127">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white py-16" data-id="element-2128">
        <div className="container mx-auto px-4" data-id="element-2129">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-center" data-id="element-2130">
            CDL Tests Information
          </h1>
          <p className="text-xl text-gray-100 text-center" data-id="element-2131">
            Knowledge Tests, Skills Tests & Requirements
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12" data-id="element-2132">
        <div className="max-w-4xl mx-auto" data-id="element-2133">
          {/* What Tests Do I Need */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2134">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-2135">
              <FileText className="mr-3 text-[#B1050B]" size={28} data-id="element-2136" />
              What Tests Do I Need To Take?
            </h2>

            <div className="space-y-6" data-id="element-2137">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-6" data-id="element-2138">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2139">
                  Original (First) CDL Applicants:
                </h3>
                <p className="text-gray-700" data-id="element-2140">
                  All drivers applying for their original commercial driver
                  license must take and pass the appropriate CDL{' '}
                  <strong data-id="element-2141">knowledge (written) tests</strong> and a{' '}
                  <strong data-id="element-2142">skills test</strong>.
                </p>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6" data-id="element-2143">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2144">
                  Drivers with CDL from Another State:
                </h3>
                <ul className="space-y-2 text-gray-700" data-id="element-2145">
                  <li data-id="element-2146">
                    • Must always take the{' '}
                    <strong data-id="element-2147">H-Hazardous Materials</strong> endorsement written
                    test to keep the H-endorsement
                  </li>
                  <li data-id="element-2148">
                    • Must pass appropriate knowledge tests when changing group
                    designator, removing restrictions, or adding endorsements
                  </li>
                  <li data-id="element-2149">
                    • May need to take CDL skills tests if upgrading group
                    designator, removing restrictions, or adding P (Passenger)
                    or S (School Bus) endorsement
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Knowledge Tests Table */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2150">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-2151">
              Knowledge Tests
            </h2>

            <p className="text-gray-700 mb-6" data-id="element-2152">
              You will take one or more knowledge tests. The following chart
              summarizes the type of tests and number of questions required
              depending on the type of vehicle you drive and the type of cargo
              you haul.
            </p>

            <div className="bg-blue-50 rounded-lg p-6 mb-6" data-id="element-2153">
              <h3 className="font-bold text-gray-900 mb-4" data-id="element-2154">
                CDL Tests by Group
              </h3>

              <div className="space-y-4" data-id="element-2155">
                <div className="bg-white rounded-lg p-4" data-id="element-2156">
                  <div className="flex justify-between items-center" data-id="element-2157">
                    <div data-id="element-2158">
                      <p className="font-bold text-blue-600 text-xl" data-id="element-2159">Group A</p>
                      <p className="text-sm text-gray-600" data-id="element-2160">
                        Includes 20-question combination vehicle test
                      </p>
                    </div>
                    <div className="text-right" data-id="element-2161">
                      <p className="text-2xl font-bold text-gray-900" data-id="element-2162">
                        70 Questions
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-2163">Skills Test: Yes</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4" data-id="element-2164">
                  <div className="flex justify-between items-center" data-id="element-2165">
                    <div data-id="element-2166">
                      <p className="font-bold text-green-600 text-xl" data-id="element-2167">
                        Group B
                      </p>
                    </div>
                    <div className="text-right" data-id="element-2168">
                      <p className="text-2xl font-bold text-gray-900" data-id="element-2169">
                        50 Questions
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-2170">Skills Test: Yes</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4" data-id="element-2171">
                  <div className="flex justify-between items-center" data-id="element-2172">
                    <div data-id="element-2173">
                      <p className="font-bold text-yellow-600 text-xl" data-id="element-2174">
                        Group C
                      </p>
                    </div>
                    <div className="text-right" data-id="element-2175">
                      <p className="text-2xl font-bold text-gray-900" data-id="element-2176">
                        50 Questions
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-2177">Skills Test: Yes</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-green-50 rounded-lg p-6" data-id="element-2178">
              <h3 className="font-bold text-gray-900 mb-4" data-id="element-2179">Endorsements</h3>

              <div className="space-y-3" data-id="element-2180">
                <div className="flex justify-between items-center bg-white rounded-lg p-3" data-id="element-2181">
                  <div className="flex items-center" data-id="element-2182">
                    <span className="bg-blue-600 text-white font-bold px-3 py-1 rounded mr-3" data-id="element-2183">
                      T
                    </span>
                    <span className="font-semibold" data-id="element-2184">Double Trailer</span>
                  </div>
                  <div className="text-right" data-id="element-2185">
                    <p className="font-bold" data-id="element-2186">20 Questions</p>
                    <p className="text-sm text-gray-600" data-id="element-2187">Knowledge only</p>
                  </div>
                </div>

                <div className="flex justify-between items-center bg-white rounded-lg p-3" data-id="element-2188">
                  <div className="flex items-center" data-id="element-2189">
                    <span className="bg-green-600 text-white font-bold px-3 py-1 rounded mr-3" data-id="element-2190">
                      P
                    </span>
                    <span className="font-semibold" data-id="element-2191">Passenger</span>
                  </div>
                  <div className="text-right" data-id="element-2192">
                    <p className="font-bold" data-id="element-2193">20 Questions</p>
                    <p className="text-sm text-gray-600" data-id="element-2194">Knowledge + Skills</p>
                  </div>
                </div>

                <div className="flex justify-between items-center bg-white rounded-lg p-3" data-id="element-2195">
                  <div className="flex items-center" data-id="element-2196">
                    <span className="bg-yellow-600 text-white font-bold px-3 py-1 rounded mr-3" data-id="element-2197">
                      S
                    </span>
                    <span className="font-semibold" data-id="element-2198">School Bus</span>
                  </div>
                  <div className="text-right" data-id="element-2199">
                    <p className="font-bold" data-id="element-2200">20 Questions</p>
                    <p className="text-sm text-gray-600" data-id="element-2201">Knowledge + Skills</p>
                  </div>
                </div>

                <div className="flex justify-between items-center bg-white rounded-lg p-3" data-id="element-2202">
                  <div className="flex items-center" data-id="element-2203">
                    <span className="bg-purple-600 text-white font-bold px-3 py-1 rounded mr-3" data-id="element-2204">
                      N
                    </span>
                    <span className="font-semibold" data-id="element-2205">Tank Vehicles</span>
                  </div>
                  <div className="text-right" data-id="element-2206">
                    <p className="font-bold" data-id="element-2207">30 Questions</p>
                    <p className="text-sm text-gray-600" data-id="element-2208">Knowledge only</p>
                  </div>
                </div>

                <div className="flex justify-between items-center bg-white rounded-lg p-3" data-id="element-2209">
                  <div className="flex items-center" data-id="element-2210">
                    <span className="bg-red-600 text-white font-bold px-3 py-1 rounded mr-3" data-id="element-2211">
                      H
                    </span>
                    <span className="font-semibold" data-id="element-2212">Hazardous Materials</span>
                  </div>
                  <div className="text-right" data-id="element-2213">
                    <p className="font-bold" data-id="element-2214">30 Questions</p>
                    <p className="text-sm text-gray-600" data-id="element-2215">Knowledge only</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-red-50 border-l-4 border-red-500 p-6 mt-6" data-id="element-2216">
              <h3 className="font-bold text-gray-900 mb-3" data-id="element-2217">Air Brakes Test</h3>
              <p className="text-gray-700 mb-3" data-id="element-2218">
                If you want to be licensed to drive vehicles with air brakes,
                you must pass a{' '}
                <strong data-id="element-2219">25-question air brake knowledge test</strong>. The
                vehicle you use for the skills test must be equipped with air
                brakes.
              </p>
              <p className="text-sm text-gray-600" data-id="element-2220">
                If either of these conditions has not been met, you will be
                restricted from operating vehicles with air brakes.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6 mt-6" data-id="element-2221">
              <h3 className="font-bold text-gray-900 mb-3" data-id="element-2222">
                Test Format & Passing Score:
              </h3>
              <ul className="space-y-2 text-gray-700" data-id="element-2223">
                <li className="flex items-start" data-id="element-2224">
                  <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-2225" />
                  <span data-id="element-2226">
                    All CDL knowledge tests are administered in written form
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2227">
                  <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-2228" />
                  <span data-id="element-2229">
                    Minimum passing score of <strong data-id="element-2230">80%</strong> is required to
                    pass each test
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2231">
                  <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-2232" />
                  <span data-id="element-2233">
                    Oral tests available on audiocassettes for limited reading
                    skills (except H-Hazardous Materials and S-School Bus)
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2234">
                  <AlertTriangle className="text-yellow-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-2235" />
                  <span data-id="element-2236">
                    H-Hazardous Materials and S-School Bus endorsements must
                    pass written knowledge test
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* CDL Skills Test */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2237">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-2238">
              <ClipboardCheck className="mr-3 text-[#B1050B]" size={28} data-id="element-2239" />
              CDL Skills Test
            </h2>

            <p className="text-gray-700 mb-6" data-id="element-2240">
              You will need to pass the CDL skills test which includes three
              parts:
            </p>

            <div className="space-y-4 mb-6" data-id="element-2241">
              <div className="bg-blue-50 rounded-lg p-6 flex items-start" data-id="element-2242">
                <span className="bg-blue-600 text-white font-bold px-4 py-2 rounded mr-4 flex-shrink-0 text-xl" data-id="element-2243">
                  1
                </span>
                <div data-id="element-2244">
                  <p className="font-bold text-gray-900 mb-2" data-id="element-2245">
                    Vehicle Inspection Test
                  </p>
                  <p className="text-gray-700" data-id="element-2246">
                    To measure your ability to perform a vehicle safety check. A
                    Vehicle Inspection Memory Aid is available and may be used
                    during the test.
                  </p>
                </div>
              </div>

              <div className="bg-green-50 rounded-lg p-6 flex items-start" data-id="element-2247">
                <span className="bg-green-600 text-white font-bold px-4 py-2 rounded mr-4 flex-shrink-0 text-xl" data-id="element-2248">
                  2
                </span>
                <div data-id="element-2249">
                  <p className="font-bold text-gray-900 mb-2" data-id="element-2250">
                    Off-Street Basic Control Skills Test
                  </p>
                  <p className="text-gray-700" data-id="element-2251">
                    Testing your ability to control the vehicle in various
                    maneuvers
                  </p>
                </div>
              </div>

              <div className="bg-yellow-50 rounded-lg p-6 flex items-start" data-id="element-2252">
                <span className="bg-yellow-600 text-white font-bold px-4 py-2 rounded mr-4 flex-shrink-0 text-xl" data-id="element-2253">
                  3
                </span>
                <div data-id="element-2254">
                  <p className="font-bold text-gray-900 mb-2" data-id="element-2255">
                    On-Street Driving Test
                  </p>
                  <p className="text-gray-700" data-id="element-2256">
                    Testing your ability to drive safely in traffic
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-red-50 border-l-4 border-red-500 p-6 mb-6" data-id="element-2257">
              <h3 className="font-bold text-gray-900 mb-3" data-id="element-2258">
                Important Testing Rules:
              </h3>
              <ul className="space-y-2 text-gray-700" data-id="element-2259">
                <li data-id="element-2260">
                  • All three parts are conducted during the same testing
                  session, in the order listed above
                </li>
                <li data-id="element-2261">
                  • Each portion must be passed before continuing to the next
                </li>
                <li data-id="element-2262">• A failure of any segment terminates testing</li>
                <li data-id="element-2263">
                  • On subsequent attempts, you must always start with the
                  vehicle inspection regardless of which part you previously
                  failed
                </li>
                <li data-id="element-2264">
                  • You are allowed <strong data-id="element-2265">one (1) attempt per day</strong>
                </li>
                <li data-id="element-2266">
                  • The Secretary of State has the right to randomly retest
                  anyone tested by a third party tester
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-lg p-6" data-id="element-2267">
              <h3 className="font-bold text-gray-900 mb-4" data-id="element-2268">
                Vehicle & Documentation Requirements:
              </h3>
              <ul className="space-y-3 text-gray-700" data-id="element-2269">
                <li className="flex items-start" data-id="element-2270">
                  <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-2271" />
                  <span data-id="element-2272">
                    You must supply an empty vehicle with a GVWR representative
                    of the type you plan to drive (including a trailer if
                    applicable)
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2273">
                  <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-2274" />
                  <span data-id="element-2275">
                    The vehicle must be equipped with at least two permanently
                    mounted seats for yourself and the examiner
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2276">
                  <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-2277" />
                  <span data-id="element-2278">
                    Safety belts are required for both driver and examiner
                    (except for examiner on buses)
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2279">
                  <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-2280" />
                  <span data-id="element-2281">
                    Applicants applying for P-Passenger endorsement must pass
                    skills test in a vehicle with seating capacity of{' '}
                    <strong data-id="element-2282">16 or more passengers</strong>, including the driver
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2283">
                  <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-2284" />
                  <span data-id="element-2285">
                    You must furnish: Michigan photo driver license, CDL
                    Temporary Instruction Permit (TIP), and Medical Examiner's
                    Certificate (DOT card, School Bus card) or medical waiver
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* CDL Restrictions */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2286">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-2287">
              <AlertTriangle className="mr-3 text-yellow-600" size={28} data-id="element-2288" />
              What CDL Restrictions Could I Receive?
            </h2>

            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6" data-id="element-2289">
              <h3 className="font-bold text-gray-900 mb-3" data-id="element-2290">
                Restriction Code 30
              </h3>
              <p className="text-gray-700 mb-3" data-id="element-2291">
                <strong data-id="element-2292">
                  "CDL-P or S endorsement valid in Group C vehicles only."
                </strong>
              </p>
              <ul className="space-y-2 text-gray-700 ml-4" data-id="element-2293">
                <li data-id="element-2294">
                  • Adding a P-Passenger endorsement to your current Group A or
                  B designation and completing your CDL skills test in a Group C
                  bus will limit the P-Passenger endorsement to operation of a
                  Group C bus
                </li>
                <li data-id="element-2295">
                  • Adding P-Passenger and S-School Bus endorsements to your
                  current Group A or B designation and completing your CDL
                  skills test in a school bus will limit both endorsements to
                  operation of Group C school buses
                </li>
              </ul>
            </div>
          </div>

          {/* Seasonal Restricted CDL */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2296">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-2297">
              Seasonal Restricted CDL
            </h2>

            <p className="text-gray-700 mb-4" data-id="element-2298">
              A special seasonal restricted CDL is available for agri-business
              employees operating Group B and C vehicles on routes within{' '}
              <strong data-id="element-2299">150 miles</strong> from the place of business.
            </p>

            <div className="space-y-4" data-id="element-2300">
              <div className="bg-blue-50 rounded-lg p-6" data-id="element-2301">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2302">Requirements:</h3>
                <ul className="space-y-2 text-gray-700" data-id="element-2303">
                  <li data-id="element-2304">
                    • Buses and school buses cannot be operated with this
                    restricted license
                  </li>
                  <li data-id="element-2305">• CDL knowledge and skills tests are NOT required</li>
                  <li data-id="element-2306">
                    • Applicants must have held a valid driver license for at
                    least <strong data-id="element-2307">one year</strong>
                  </li>
                  <li data-id="element-2308">
                    • Must possess a clean driving record for the{' '}
                    <strong data-id="element-2309">three-year period</strong> preceding application
                  </li>
                </ul>
              </div>

              <div className="bg-green-50 rounded-lg p-6" data-id="element-2310">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2311">
                  Validity Period:
                </h3>
                <p className="text-gray-700 mb-2" data-id="element-2312">
                  This limited CDL must be applied for each calendar year.
                  Within a one-year period, applicants can choose:
                </p>
                <ul className="space-y-1 text-gray-700 ml-4" data-id="element-2313">
                  <li data-id="element-2314">
                    • Valid for <strong data-id="element-2315">180 days</strong> from date of issue, OR
                  </li>
                  <li data-id="element-2316">
                    • Valid for seasonal periods:{' '}
                    <strong data-id="element-2317">April 2 - June 30</strong> and{' '}
                    <strong data-id="element-2318">September 2 - November 30</strong>
                  </li>
                </ul>
              </div>

              <div className="bg-yellow-50 rounded-lg p-6" data-id="element-2319">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2320">
                  Permitted Hazardous Materials (without H or X endorsements):
                </h3>
                <ul className="space-y-2 text-gray-700" data-id="element-2321">
                  <li data-id="element-2322">
                    • Diesel motor fuel in quantities of{' '}
                    <strong data-id="element-2323">1,000 gallons or less</strong>
                  </li>
                  <li data-id="element-2324">
                    • Liquid fertilizers in quantities of{' '}
                    <strong data-id="element-2325">3,000 gallons or less</strong>
                  </li>
                  <li data-id="element-2326">
                    • Solid fertilizers that are not transported with any
                    organic substance
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* License Fees */}
          <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white rounded-lg shadow-xl p-8 mb-8" data-id="element-2327">
            <h2 className="text-3xl font-bold mb-6 flex items-center" data-id="element-2328">
              <DollarSign className="mr-3" size={32} data-id="element-2329" />
              What License Fees Do I Pay?
            </h2>

            <div className="space-y-4" data-id="element-2330">
              <div className="bg-white/10 rounded-lg p-4 flex justify-between items-center" data-id="element-2331">
                <span className="font-semibold" data-id="element-2332">Base Operator</span>
                <span className="text-2xl font-bold" data-id="element-2333">$25</span>
              </div>

              <div className="bg-white/10 rounded-lg p-4 flex justify-between items-center" data-id="element-2334">
                <span className="font-semibold" data-id="element-2335">Base Chauffeur</span>
                <span className="text-2xl font-bold" data-id="element-2336">$35</span>
              </div>

              <div className="bg-white/10 rounded-lg p-4" data-id="element-2337">
                <p className="text-center font-bold mb-2" data-id="element-2338">PLUS</p>
              </div>

              <div className="bg-white/10 rounded-lg p-4 flex justify-between items-center" data-id="element-2339">
                <span className="font-semibold" data-id="element-2340">
                  CDL Vehicle Group Designation
                </span>
                <span className="text-2xl font-bold" data-id="element-2341">$25</span>
              </div>

              <div className="bg-white/10 rounded-lg p-4" data-id="element-2342">
                <div className="flex justify-between items-center mb-2" data-id="element-2343">
                  <span className="font-semibold" data-id="element-2344">
                    CDL Endorsements (if any)
                  </span>
                  <span className="text-2xl font-bold" data-id="element-2345">$5 each</span>
                </div>
                <p className="text-sm text-gray-200 text-right" data-id="element-2346">
                  $10 for "X" Endorsement
                </p>
              </div>

              <div className="bg-white/10 rounded-lg p-4 flex justify-between items-center" data-id="element-2347">
                <span className="font-semibold" data-id="element-2348">
                  Correction Fee (if applicable)
                </span>
                <span className="text-2xl font-bold" data-id="element-2349">$18</span>
              </div>
            </div>
          </div>

          {/* Third Party Testing */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2350">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-2351">
              Third Party Driver Skills Test Fee Requirements
            </h2>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-6" data-id="element-2352">
              <p className="text-gray-800" data-id="element-2353">
                Third party driver skills test fees and refund policies vary. Be
                sure to discuss and understand your test fees and scheduling
                requirements with the third party organization that you have
                selected before taking your test.
              </p>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-lg shadow-xl p-8 text-center" data-id="element-2354">
            <h2 className="text-3xl font-bold mb-4" data-id="element-2355">
              Need Help with CDL Testing?
            </h2>
            <p className="text-xl mb-6" data-id="element-2356">
              Expert guidance on CDL tests, requirements, and preparation
            </p>
            <a href="tel:2484788382" className="inline-flex items-center bg-[#B1050B] hover:bg-[#8A0408] text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg" data-id="element-2357">
              <Phone className="mr-3" size={24} data-id="element-2358" />
              Call (248) 478-8382
            </a>
            <p className="mt-4 text-gray-300" data-id="element-2359">
              24175 Industrial Park Dr., Farmington Hills, MI 48335
            </p>
          </div>
        </div>
      </div>
    </div>;
}